import { createContext, useCallback, useContext } from 'react';
import { useLocalStorage } from 'react-use';

import { useWorkspace } from '@app/hooks/workspace';

interface MainPageHeaderState {
  documents: 0 | 1;
  externalDocuments: 0 | 1;
  templates: 0 | 1;
  bulkRequests: 0 | 1;
  links: 0 | 1;
  approvals: 0 | 1;
  editingDocuments: 0 | 1;
  signatures: 0 | 1;
  drafts: 0 | 1;
}
const initialValue: MainPageHeaderState = {
  documents: 0,
  externalDocuments: 0,
  templates: 0,
  bulkRequests: 0,
  links: 0,
  approvals: 0,
  editingDocuments: 0,
  signatures: 0,
  drafts: 0,
};

const key = 'modusign__main_page_header_state';

const MainPageHeaderContext = createContext<
  [state: MainPageHeaderState, set: (nextState: Partial<MainPageHeaderState>) => void] | null
>(null);

export function MainPageHeaderProvider({ children }: { children: React.ReactNode }) {
  const isPrivate = useWorkspace().type === 'PRIVATE';
  const [state, set] = useLocalStorage<MainPageHeaderState>(key, initialValue);
  const safeSet = useCallback(
    (nextState: Partial<MainPageHeaderState>) => set(state && { ...state, ...nextState }),
    [set, state]
  );

  const headerFilterState = isPrivate ? initialValue : state ?? initialValue; // private workspace에서는 header filter를 사용하지 않습니다.

  return (
    <MainPageHeaderContext.Provider value={[headerFilterState, safeSet]}>
      {children}
    </MainPageHeaderContext.Provider>
  );
}

export function useMainPageHeaderState() {
  const context = useContext(MainPageHeaderContext);
  if (!context) {
    throw new Error('useMainPageHeaderState must be called in MainPageHeaderProvider.');
  }
  return context;
}
